import { useState, useEffect } from "react";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Textarea } from "@/components/base/Textarea";
import { Alert } from "@/components/base/Alert";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINRNumber } from "@/utils/format";
import { collectionService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

export interface ReversalPaymentTarget {
  id: string;
  receiptNumber: string;
  memberName?: string;
  memberCode?: string;
  amount: number;
  mode?: string;
  date?: string;
  installmentInfo?: string;
}

interface ReversePaymentModalProps {
  open: boolean;
  onClose: () => void;
  target: ReversalPaymentTarget | null;
  onSuccess: (reversed: ReversalPaymentTarget) => void;
}

const COMMON_REASONS = [
  "Wrong amount entered",
  "Entered for wrong member",
  "Duplicate payment entry",
  "Payment mode incorrect",
  "Cheque / Online transfer bounced",
  "Cash not received",
];

export function ReversePaymentModal({ open, onClose, target, onSuccess }: ReversePaymentModalProps) {
  const { user } = useAuth();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  if (!target) return null;

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Please provide a reason for reversing this transaction.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await collectionService.reverseReceipt(target.id, trimmed, user?.name ?? "Admin");
      toast.warning(
        "Transaction Reverted",
        `Receipt ${target.receiptNumber} (₹${formatINRNumber(target.amount)}) has been voided. Money can now be collected again.`
      );
      onSuccess(target);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reverse transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      title="Delete / Revert Collection Transaction"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            loading={submitting}
            icon={<i className="ri-arrow-go-back-line" />}
          >
            Confirm Reversal
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning" title="Audit Notice">
          Reversing this transaction will mark receipt{" "}
          <strong className="font-semibold">{target.receiptNumber}</strong> as void, deduct{" "}
          <strong className="font-semibold">₹{formatINRNumber(target.amount)}</strong> from this member's
          installment, and restore their balance so you can collect the money again.
        </Alert>

        {/* Transaction Summary Card */}
        <div className="rounded-lg border border-background-200 bg-background-50/70 p-3.5 space-y-2">
          <div className="flex items-start justify-between gap-2 border-b border-background-200 pb-2">
            <div>
              <span className="text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Receipt No</span>
              <p className="tnum text-sm font-bold text-foreground-900">{target.receiptNumber}</p>
            </div>
            <div className="text-right">
              <span className="text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Amount Paid</span>
              <p className="text-base font-bold text-danger-700">
                <CurrencyDisplay amount={target.amount} />
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs pt-1">
            {target.memberName && (
              <div>
                <span className="text-foreground-500">Member: </span>
                <span className="font-semibold text-foreground-900">{target.memberName}</span>
                {target.memberCode && <span className="text-foreground-400"> ({target.memberCode})</span>}
              </div>
            )}
            {target.mode && (
              <div>
                <span className="text-foreground-500">Payment Mode: </span>
                <span className="font-medium text-foreground-900 uppercase">{target.mode.replace("_", " ")}</span>
              </div>
            )}
            {target.date && (
              <div>
                <span className="text-foreground-500">Date: </span>
                <span className="font-medium text-foreground-800">{formatDate(target.date)}</span>
              </div>
            )}
            {target.installmentInfo && (
              <div>
                <span className="text-foreground-500">Target: </span>
                <span className="font-medium text-foreground-800">{target.installmentInfo}</span>
              </div>
            )}
          </div>
        </div>

        {/* Quick Reason Chips */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Select a common reason:
          </label>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  reason === r
                    ? "border-primary-600 bg-primary-50 text-primary-700 font-semibold"
                    : "border-background-300 bg-white text-foreground-600 hover:border-background-400 hover:bg-background-100"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Reason Textarea */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1">
            Reason for Reversal / Deletion <span className="text-danger-600">*</span>
          </label>
          <Textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Explain why this collection transaction is being deleted or reversed..."
            rows={3}
            autoFocus
          />
          {error && <p className="mt-1 text-xs text-danger-600 font-medium">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}
